import { ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import { getPixelValue, Input, spacing } from '../..';
import { Layout } from '../Layout/Layout';
import { SELECT_ITEM_HEIGHT } from '../Select/Item.css';
import { ANCHOR_OFFSET, POPOVER_OFFSET } from '../Select/SelectContent';
import { Separator } from '../Separator/Separator';
import { NotFound } from './NotFound';
import * as styles from './Phone.css';
import { PhoneCountryListItem } from './PhoneCountryListItem';
import type { PhoneCountryListItemWrapperProps } from './PhoneCountryListItemWrapper';
import { PhoneTriggerWithSeparator } from './PhoneTriggerWithSeparator';

export interface PhoneCountryDropdownProps extends PhoneCountryListItemWrapperProps {
  readonly open: boolean;
}

const DROPDOWN_OPEN_CLOSE_DELAY: Milliseconds = 100;

export const PhoneCountryDropdown = forwardRef<HTMLDivElement, PhoneCountryDropdownProps>(
  (
    {
      open,
      placeholder,
      formattedSelectedCountry,
      disabled,
      hasError,
      searchCountryPlaceholder,
      setState,
      searchKey,
      countriesListMatcher,
      notFound,
      maxVisibleContentItems,
      onSelectCountry,
      selectedCountry,
      phoneInputRef,
    },
    ref,
  ) => {
    const searchCountryInputRef = useRef<HTMLInputElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const focusSearchCountryInput = useCallback(() => {
      timeoutRef.current = setTimeout(() => {
        searchCountryInputRef.current?.focus();
      }, DROPDOWN_OPEN_CLOSE_DELAY);
    }, []);

    const focusPhoneInput = useCallback(() => {
      timeoutRef.current = setTimeout(() => {
        phoneInputRef.current?.focus({ preventScroll: true });
      }, DROPDOWN_OPEN_CLOSE_DELAY);
    }, [phoneInputRef]);

    const listBoxHeight = useMemo(
      () => Math.min(maxVisibleContentItems ?? 5, countriesListMatcher.length) * getPixelValue(SELECT_ITEM_HEIGHT),
      [maxVisibleContentItems, countriesListMatcher],
    );

    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    useEffect(() => {
      if (open) {
        focusSearchCountryInput();
      }
    }, [open, focusSearchCountryInput]);

    return (
      <>
        <ListboxButton
          aria-label={`${placeholder}-country`}
          as={PhoneTriggerWithSeparator}
          country={formattedSelectedCountry}
          disabled={disabled}
          hasError={hasError}
          isOpen={open}
          isSingleCountry={false}
          placeholder={placeholder}
        />

        <ListboxOptions
          anchor={{
            gap: POPOVER_OFFSET,
            to: 'bottom start',
            offset: ANCHOR_OFFSET,
            padding: spacing.s24,
          }}
          className={styles.phonePopover}
          style={assignInlineVars({
            [styles.maxWidth]: ref && typeof ref === 'object' && ref.current ? `${ref.current.offsetWidth}px` : '100%',
          })}
        >
          <div className={styles.searchCountryInputWrapper}>
            <Input
              className={styles.searchCountryInput}
              floatingLabel={false}
              icon="search"
              onChange={e => setState(s => ({ ...s, searchKey: e.target.value }))}
              onClear={() => setState(s => ({ ...s, searchKey: '' }))}
              placeholder={searchCountryPlaceholder}
              ref={searchCountryInputRef}
              value={searchKey}
            />
          </div>
          <Separator className={styles.searchInputSeparator} color="$borderEnabled" />

          {countriesListMatcher.length === 0 && notFound?.label != null && <NotFound {...notFound} />}

          <Layout aria-label="Phone number" className={styles.phoneListBox} height={`${listBoxHeight}px`}>
            {countriesListMatcher.map(item => (
              <ListboxOption key={item.iso} value={item.iso}>
                <PhoneCountryListItem
                  country={item}
                  key={item.iso}
                  onSelectCountry={country => {
                    onSelectCountry(country);
                    focusPhoneInput();
                  }}
                  selected={item.iso === selectedCountry}
                />
              </ListboxOption>
            ))}
          </Layout>
        </ListboxOptions>
      </>
    );
  },
);
